import * as Sentry from "@sentry/react";
import { useMutation } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useCallback, useState } from "react";
import { useTranslations } from "use-intl";

import { api } from "@/api/api";
import {
  PurchaseStatus,
  type PaymentMethod,
  type PaymentResult,
} from "@/app/payments/types";
import { platformApi } from "@/app/platform";
import { showSnackbar } from "@/contexts/SnackbarContext";
import { useCheckoutPayment } from "@/hooks/useCheckoutPayment";
import { HotelTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";
import type { TemporallyStoreHotelBookingDetailsBodyInputDto } from "@/types/codegen";
import { updatePersonalData } from "@/utils/bookings";

type PaymentParams = Pick<
  TemporallyStoreHotelBookingDetailsBodyInputDto,
  "rooms" | "availabilityToken" | "searchResultKey" | "searchResultToken"
> & {
  offerId: string;
  hotelId: string;
  checkInDate: Date;
  promoCode?: string;
};

type BnplPaymentParams = PaymentParams & {
  sessionId: string;
  otp: string;
};

type Props = {
  onSuccess: (result: PaymentResult) => void;
  initialAvailabilityToken?: string | null;
  method?: PaymentMethod;
};

export const useHotelBooking = ({
  initialAvailabilityToken,
  method,
  onSuccess,
}: Props) => {
  const t = useTranslations();
  const [checkoutPurchasePending, setCheckoutPurchasePending] = useState(false);

  const { flowComponent, isLoadingForm } = useCheckoutPayment({
    availabilityToken: initialAvailabilityToken,
    serviceType: "hotel",
    enabled: method === "checkout",
    onSuccess,
    onSubmit: () => setCheckoutPurchasePending(true),
  });

  const handleError = useCallback(
    (error: unknown) => {
      console.error("Error in booking or payment process:", error);
      Sentry.captureException(error);
      showSnackbar({
        type: "error",
        content: t("hotels.failed"),
      });
    },
    [t],
  );

  const prebookHotel = ({
    offerId,
    hotelId,
    rooms,
    availabilityToken,
    searchResultKey,
    searchResultToken,
    checkInDate,
  }: PaymentParams) =>
    api.post(`/hotel/${offerId}/book`, {
      availabilityToken,
      searchResultKey,
      searchResultToken,
      checkInDate: dayjs(checkInDate).format("YYYY-MM-DD"),
      hotelId: parseInt(hotelId),
      rooms,
    } satisfies TemporallyStoreHotelBookingDetailsBodyInputDto);

  const checkoutPayment = {
    // TODO: promoCode is not being passed to order creation, checkout payment must be refactored before it's possible
    submit: async ({ promoCode, ...params }: PaymentParams) => {
      try {
        if (!flowComponent) {
          return;
        }

        const wasSuccess = await updatePersonalData();
        if (!wasSuccess) {
          return {
            orderId: "",
            status: PurchaseStatus.CANCELLED,
          };
        }

        await prebookHotel(params);
        flowComponent.submit();
      } catch (error) {
        handleError(error);
        setCheckoutPurchasePending(false);
      }
    },
    isLoadingCheckoutForm: isLoadingForm,
    isPending: checkoutPurchasePending,
  };

  const qiPayment = useMutation({
    mutationFn: async (params: PaymentParams) => {
      const wasSuccess = await updatePersonalData();
      if (!wasSuccess) {
        return {
          orderId: "",
          status: PurchaseStatus.CANCELLED,
        };
      }

      await prebookHotel(params);

      const { promoCode, availabilityToken } = params;

      // Use standard payment flow
      return platformApi.getPaymentProvider("standard").initiatePayment({
        availabilityTokens: [availabilityToken],
        serviceType: "hotel",
        promoCode,
        onSuccess: ({ orderId }) => {
          rudderAnalytics.track(HotelTrackingEvents.USER_SUCCESSFULY_PAID, {
            payment_method: "qiAccount",
            payment_status: "Success",
            transaction_id: orderId,
          });
        },
        onFailure: ({ orderId }) => {
          rudderAnalytics.track(HotelTrackingEvents.USER_UNSUCCESSFULY_PAID, {
            payment_method: "qiAccount",
            payment_status: "fail",
            transaction_id: orderId,
          });
        },
      });
    },
    onSuccess,
    onError: handleError,
  });

  const bnplPayment = useMutation({
    mutationFn: async (params: BnplPaymentParams) => {
      const wasSuccess = await updatePersonalData();
      if (!wasSuccess) {
        return {
          orderId: "",
          status: PurchaseStatus.CANCELLED,
        };
      }

      await prebookHotel(params);

      const { promoCode, otp, sessionId, availabilityToken } = params;

      return platformApi.getPaymentProvider("bnpl").initiatePayment({
        availabilityTokens: [availabilityToken],
        sessionId,
        otp,
        promoCode,
      });
    },
    onSuccess,
    onError: handleError,
  });

  return {
    qiPayment,
    checkoutPayment,
    bnplPayment,
  };
};
